import React, { FC, ReactNode, useEffect, useState } from 'react';
import OrangeTrafficLight from './OrangeTrafficLight';
import RedTrafficLight from './RedTrafficLight';
import YellowTrafficLight from './YellowTrafficLight';
import { useNotificationsContext } from 'shared/context/notificationContext';
import clsx from 'clsx';
import { ColorState } from 'app/viaje/viajeTraslado/viajeTraslado/components/ViajeTrasladoBaseFilters';
import VioletTrafficLight from './VioletTrafficLight';

interface TotalsTrafficLightsProps {
  onClick: (filter: any) => void;
  filters?: any;
}

const LampSocket: FC<{ children: ReactNode }> = ({ children }) => (
  <div
    style={{
      position: 'relative',
      padding: '4px 6px',
      borderRadius: '999px',
      background:
        'radial-gradient(circle at 30% 30%, #1a1a1a 0%, #050505 75%, #000 100%)',
      boxShadow:
        'inset 0 0 6px 1px rgba(0, 0, 0, 0.95), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
      border: '1px solid rgba(0, 0, 0, 0.8)'
    }}
  >
    <div
      aria-hidden
      style={{
        position: 'absolute',
        top: '-4px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '88%',
        height: '7px',
        borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px',
        background:
          'linear-gradient(180deg, #2a2a2a 0%, #111 60%, #000 100%)',
        boxShadow: '0 2px 3px rgba(0, 0, 0, 0.7)',
        pointerEvents: 'none'
      }}
    />
    {children}
  </div>
);

const TotalsTrafficLights: FC<TotalsTrafficLightsProps> = ({
  onClick,
  filters
}) => {
  const {
    totalStatesReservations,
    stopAnimationTrafficLights,
    isRefreshingTrafficLights,
    trafficLightsRefreshError
  } = useNotificationsContext();
  const { red, orange, yellow, violet } = totalStatesReservations;
  const [activeLight, setActiveLight] = useState<ColorState | string | null>();

  useEffect(() => {
    setActiveLight(filters?.color || filters?.reservationId || null);
  }, [filters]);

  const handleLightClick = (color: ColorState, reservationId?: number | null) => {
    stopAnimationTrafficLights(color);

    if (color === ColorState.RED) {
      onClick(
        reservationId
          ? { reservationId: reservationId }
          : { color: activeLight === ColorState.RED ? '' : ColorState.RED }
      );
    } else {
      onClick({ color: activeLight === color ? '' : color });
    }
  };

  const allCero = red.total === 0 && orange.total === 0 && yellow.total === 0 && violet.total === 0;

  return (
    <div
      className={clsx('relative', allCero && 'hidden')}
      style={{
        position: 'absolute',
        top: '50%',
        right: '50%',
        transform: 'translate(50%, -50%)',
        zIndex: 100,
        width: 'fit-content',
      }}
    >
      {/* Casing body */}
      <div
        className="flex gap-3 justify-evenly items-center relative"
        style={{
          height: '52px',
          padding: '0px 18px',
          borderRadius: '46px',
          background:
            'linear-gradient(180deg, #3a3a3a 0%, #1a1a1a 45%, #0a0a0a 100%)',
          border: '2px solid #000',
          boxShadow:
            'inset 0 2px 3px rgba(255, 255, 255, 0.18), inset 0 -3px 6px rgba(0, 0, 0, 0.8), 0 6px 18px rgba(0, 0, 0, 0.6)'
        }}
      >
        {trafficLightsRefreshError && !isRefreshingTrafficLights && (
          <span
            title={trafficLightsRefreshError}
            style={{
              position: 'absolute',
              top: 2,
              right: 6,
              fontSize: 10,
              color: 'rgba(255, 120, 120, 1)',
              cursor: 'default'
            }}
          >
            ⚠ Error
          </span>
        )}
          <LampSocket>
            <RedTrafficLight
              total={red.total}
              statuses={red.statuses}
              onClick={(reservationId) => {
                handleLightClick(ColorState.RED, reservationId);
              }}
              isActive={activeLight === ColorState.RED}
              reservationIdSelected={filters?.reservationId || null}
              animate={red.animate}
              isLoading={isRefreshingTrafficLights}
            />
          </LampSocket>
          <LampSocket>
            <OrangeTrafficLight
              total={orange.total}
              onClick={() => {
                handleLightClick(ColorState.ORANGE);
              }}
              isActive={activeLight === ColorState.ORANGE}
              animate={orange.animate}
              isLoading={isRefreshingTrafficLights}
            />
          </LampSocket>
          <LampSocket>
            <YellowTrafficLight
              total={yellow.total}
              onClick={() => {
                handleLightClick(ColorState.YELLOW);
              }}
              isActive={activeLight === ColorState.YELLOW}
              animate={yellow.animate}
              isLoading={isRefreshingTrafficLights}
            />
          </LampSocket>
          <LampSocket> 
            <VioletTrafficLight
              total={violet.total}
              onClick={() => {
                handleLightClick(ColorState.VIOLET);
              }}
              isActive={activeLight === ColorState.VIOLET}
              animate={violet.animate}
              isLoading={isRefreshingTrafficLights}
            />
          </LampSocket>
      </div>
    </div>
  );
};

export default TotalsTrafficLights;
